import { FILE_INPUT_ACCEPT } from '@shared/all/uploadTypes';

import toast from './toast';

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export type PickResult = {
  uri: string;
  fileName: string | null;
  file?: File;
  width?: number;
  height?: number;
  isVideo?: boolean;
};

/**
 * Progress + cancellation hooks for an S3 upload. Both platforms accept this;
 * native uses expo-file-system 56's task-based uploader, web uses XHR.
 */
export type UploadProgressOptions = {
  /** Called with a 0–1 fraction as bytes are sent. */
  onProgress?: (fraction: number) => void;
  /** Abort the in-flight upload; rejects the promise with an AbortError. */
  signal?: AbortSignal;
};

export type PickImageOptions = {
  cropping?: boolean;
  width?: number;
  height?: number;
  circularCrop?: boolean;
};

export type PickMediaOptions = {
  mediaType?: 'photo' | 'video' | 'any';
};

/**
 * Extracts dimensions from any URI (blob:, data:, http:, etc.).
 * Tries loading as an image first, then falls back to video.
 */
export const getDimensionsFromUri = (
  uri: string
): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => {
      // Not an image — try as video
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () =>
        resolve({ width: video.videoWidth, height: video.videoHeight });
      video.onerror = () => resolve(null);
      video.src = uri;
    };
    img.src = uri;
  });
};

/**
 * Duration in seconds for a video URI, or null if unreadable.
 */
export const getVideoDurationFromUri = (
  uri: string
): Promise<number | null> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      resolve(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => resolve(null);
    video.src = uri;
  });
};

/**
 * Opens a file dialog for image selection on web.
 * Returns { uri (objectURL), fileName, file, width, height } or null.
 */
/**
 * Takes an image off the clipboard and stages it as a picked file. Returns
 * null when the clipboard holds no image (or the browser refuses to read it —
 * Firefox has no `navigator.clipboard.read`).
 *
 * The platform pair of this is what mobile needs: React Native's TextInput has
 * no `onPaste`, so a screenshot can only be pasted through an explicit action.
 */
export const pasteImage = async (): Promise<PickResult | null> => {
  try {
    const items = await navigator.clipboard?.read?.();
    if (!items) return null;
    for (const item of items) {
      const type = item.types.find((t) => t.startsWith('image/'));
      if (!type) continue;
      const blob = await item.getType(type);
      if (blob.size > MAX_FILE_SIZE) {
        toast('File exceeds 10MB limit', '', 'error');
        return null;
      }
      const extension = type.split('/')[1] || 'png';
      const file = new File([blob], `pasted-image.${extension}`, { type });
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      return {
        uri,
        fileName: file.name,
        file,
        width: dimensions?.width,
        height: dimensions?.height
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const pickImage = (
  _options?: PickImageOptions
): Promise<PickResult | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      if (file.size > MAX_FILE_SIZE) {
        toast('File exceeds 10MB limit', '', 'error');
        return resolve(null);
      }
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      resolve({
        uri,
        fileName: file.name,
        file,
        width: dimensions?.width,
        height: dimensions?.height
      });
    };
    input.click();
  });
};

/**
 * Opens a file dialog for any supported file type on web.
 */
export const pickFile = (
  _options?: PickImageOptions
): Promise<PickResult | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = FILE_INPUT_ACCEPT;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      if (file.size > MAX_FILE_SIZE) {
        toast('File exceeds 10MB limit', '', 'error');
        return resolve(null);
      }
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      resolve({
        uri,
        fileName: file.name,
        file,
        width: dimensions?.width,
        height: dimensions?.height
      });
    };
    input.click();
  });
};

/**
 * Opens a file dialog for image and video selection on web.
 */
export const pickMedia = (
  _options?: PickMediaOptions
): Promise<PickResult | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const isVideo = file.type.startsWith('video/');
      const sizeLimit = isVideo ? 100 * 1024 * 1024 : MAX_FILE_SIZE;
      const limitLabel = isVideo ? '100MB' : '10MB';
      if (file.size > sizeLimit) {
        toast(`File exceeds ${limitLabel} limit`, '', 'error');
        return resolve(null);
      }
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      resolve({
        uri,
        fileName: file.name,
        file,
        width: dimensions?.width,
        height: dimensions?.height,
        isVideo
      });
    };
    input.click();
  });
};

/**
 * On web, passthrough for data: URIs. For blob/object URLs, fetches and converts.
 */
export const uriToBase64 = async (uri: string): Promise<string> => {
  if (uri.startsWith('data:')) return uri;
  const response = await fetch(uri);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to convert URI to base64'));
    reader.readAsDataURL(blob);
  });
};

/**
 * Ensures a base64 string has the data: URL prefix.
 */
export const ensureDataUrl = (base64: string): string => {
  if (base64.startsWith('data:')) return base64;
  return `data:image/jpeg;base64,${base64}`;
};

/**
 * Uploads a file directly to R2 using a presigned PUT URL and its signed
 * headers.
 *
 * PUT, not a form POST: R2 does not implement POST Object, so there is no
 * policy-and-fields upload to send. The bytes are the whole request body and
 * `headers` must be echoed byte-for-byte — every one of them is inside the
 * signature, so an altered Content-Type, Cache-Control or Content-Length is a
 * 403 rather than a stored object.
 *
 * Web variant — sends `source.blob`. `source.uri` was never uploadable here
 * (web's FormData silently dropped URI strings), and it still is not, so the
 * same early throw guards the same footgun.
 */
export const uploadToStorage = async (
  source: { blob: Blob; uri?: string },
  url: string,
  headers: Record<string, string>,
  opts?: UploadProgressOptions
): Promise<number> => {
  if (source.uri) {
    throw new Error(
      'uploadToStorage (web): source.uri is not supported — pass `blob` only. ' +
        'If you have a URI, fetch it to a Blob first.'
    );
  }

  // XHR (not fetch) so we can report upload progress and support cancellation.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    // Content-Length is set by the browser from the body and is forbidden to
    // set by hand; it is signed, so sending a body of a different length still
    // fails the signature. Everything else goes on the wire verbatim.
    for (const [k, v] of Object.entries(headers)) {
      if (k.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(k, v);
    }

    if (opts?.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
      };
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () =>
      reject(new DOMException('Upload aborted', 'AbortError'));

    if (opts?.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(source.blob);
  });

  return source.blob.size;
};
